import { ControllerHelper, GroupDownloadDto, PostController, PostDownloadDto } from 'collaboration-service';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import { LinkDto } from "imaginarity-azure";
import { Icon, Image, LoaderInline, getColor, lightenColor, styled } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'services/ApplicationState/ApplicationState';
import { getGroupsOfType, getLink, shallowCompare } from 'services/Helpers';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { useThemePart } from 'services/useAppTheme';
import useTreeView, { MapToTreeElements, TreeElement } from 'services/useTreeView';


export interface usePostSelectionProps<PostType> {
    namespace?: string;
    selectedIds?: string[];
    multiple?: boolean;
    toggleItem?: (id: string, selected: boolean) => void;
    postTypes: string[] | string;
    groupTypes: string[] | string;
    getThumb: (post: PostType) => { links: LinkDto[] };
}

const mapper = (s: ApplicationState) => ({
    contentLanguage: s.contentLanguage,
    userGroups: s.userGroups,
    user: s.user,
});

const BItem = styled.div<{ selected: boolean }>`
    ${p => p.selected ? "font-weight: bolder;" : ""}
    cursor: pointer;
    background: ${p => getColor(p, "@mainBackground")};
    padding: 5px;
    transition: all 0.2s ease-out;
    &:hover{
        background-color: ${p => getColor(p, "@veryLightGrey")};
    }
`;

const Item = styled(BItem)`
    padding: 0px;
    display: grid;
    grid-template-columns: 40px 1fr;
    grid-template-rows: 40px;
    column-gap: 10px;
    row-gap: 10px;
    background: ${p => p.selected ? lightenColor(p, "@veryLightGrey", 98) : getColor(p, "@mainBackground")};
    border-left: 3px solid ${p => p.selected ? getColor(p, "@accent") : getColor(p, "@mainBackground")};
    margin-right: 10px;
    &:hover{
        border-left: 3px solid ${p => p.selected ? getColor(p, "@accent") : lightenColor(p, "@veryLightGrey", 98)};
    }
`;

const T = styled.div`
    line-height: 40px;
    width: calc(100% - 20px);
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const I = styled.div`
    height: 40px;
    line-height: 40px;
`;


const TreeItemsContainer = styled.div<{ showBox: boolean }>`
    //height: ${p => p.showBox ? "calc(100% - 75px)" : "100%"};
    //overflow-y: auto;
`;

const Spacer = styled.div`
    width: 30px;
`;
const usePostSelection = <PostType extends PostDownloadDto = PostDownloadDto>(p: usePostSelectionProps<PostType>) => {
    const { multiple, namespace, selectedIds, toggleItem, postTypes, getThumb, groupTypes } = p;
    const { t } = useImgI18N(namespace ?? "general");
    const { contentLanguage, userGroups, user } = useSelector(mapper, shallowCompare)
    const emptyImageUrl = useThemePart(t => t.emptyImageUrl);

    const getBaseItems = React.useMemo(() => {
        const groups = getGroupsOfType(user, userGroups, groupTypes);
        return async () => MapToTreeElements<GroupDownloadDto, PostType>(groups);
    }, [user, userGroups, groupTypes]);

    const { renderItem, renderBaseItem, itemLoading, expander } = React.useMemo(() => {
        return {
            renderItem: (i: TreeElement<PostType, PostType>, selected: boolean, toggleSelect: () => void) => {
                const thumb = getLink(getThumb(i.item), "self")?.uri ?? emptyImageUrl;
                const sel = selected || _.find(selectedIds, id => id === i.item.id) !== undefined;
                const click = toggleItem ? () => toggleItem(i.item.id, sel) : undefined;
                return (
                    <Item selected={sel} onClick={click}>
                        <I>
                            <Image
                                src={thumb}
                                borderColor="@veryLightGrey"
                                thumbnail={true}
                                squared={true}
                                width={"40px"}
                                floated="left"
                            />
                        </I>
                        <T>
                            <SafeHTML
                                allowedTags={[]}
                                allowedAttributes={{}}
                                html={getTranslated(i.item.headlines, contentLanguage)?.text ?? ""} />
                        </T>
                    </Item>
                )
            },
            renderBaseItem: (i: TreeElement<GroupDownloadDto, PostType>) => {
                return (
                    <BItem selected={false}>
                        <Icon name="folder" marginTop={0} style={{ float: "left", marginRight: 5 }} />
                        {i.item.name}
                    </BItem>
                )
            },
            itemLoading: (loading: boolean) => <div style={{ margin: "5px 10px 5px 25px" }}><LoaderInline active={loading} infoText={t("loading files ...")} /></div>,
            expander: (expanded: boolean) => <Icon name={expanded ? "chevron down" : "chevron right"} marginTop={4} style={{ float: "left", cursor: "pointer" }} />,

        }
    }, [t, contentLanguage, emptyImageUrl, toggleItem, getThumb, selectedIds]);



    const getChildrenFromBase = React.useMemo(() => async (item: GroupDownloadDto, currentChildren?: TreeElement<PostType, PostType>[]) => {
        if (currentChildren) {
            return currentChildren;
        }
        let token: string | undefined;
        const videos: PostDownloadDto[] = [];
        do {
            const res = await ControllerHelper.singleCall({
                filter: {
                    onlyInGroups: [item.id],
                    includedPostTypes: _.isArray(postTypes) ? postTypes : [postTypes],
                },
                token: { token }
            }, PostController.GetPagedForAllGroups);
            token = res.data;
            videos.push(...res.elements);

        } while (token !== null)

        const mapVideos = (posts: PostDownloadDto[]): TreeElement<PostType, PostType>[] => {
            const entries: TreeElement<PostType, PostType>[] = [];
            _.forEach(posts, e => {
                entries.push({
                    notExpandable: true,
                    expanded: false,
                    loading: false,
                    item: e as any as PostType,
                    children: [],
                    selectable: toggleItem === undefined,
                    notToggleable: toggleItem !== undefined
                });
            });
            return [...entries];
        }

        const toRet = mapVideos(videos);
        return toRet;
    }, [toggleItem, postTypes]);
    const getChildrenFromChild = React.useMemo(() => async (item: PostType, currentChildren?: TreeElement<PostType, PostType>[]) => {
        return currentChildren ?? [];
    }, []);

    const {
        toggleBaseItem,
        //        toggleItem,
        //        loading,
        items,
        TreeItems,
        selectedItems,
        toggleSelectItem,
    } = useTreeView<GroupDownloadDto, PostType>({
        getBaseItems,
        getChildrenFromBase,
        getChildrenFromChild,
        //hiddenBaseItems,
        spacer: <Spacer />, // <div style={{ width: 30, height: "100%" }}><div style={{ width: 15, height: "100%", borderRight: "1px solid black", display: "inline-block" }} /><div style={{ width: 15, height: "100%", display: "inline-block" }} /></div>,
        expander,
        renderBaseItem,
        renderItem,
        itemLoading,

        multipleSelect: multiple ?? false,

        //filterItem: (item: FileEntryDownloadDto, filter: string) => item.name.toLowerCase().includes(filter.toLowerCase()),
    });

    return {
        toggleBaseItem,
        items,
        selectedItems,
        toggleSelectItem,
        Browse: () => (
            <TreeItemsContainer showBox={selectedItems.length > 0}>
                <TreeItems />
            </TreeItemsContainer>
        ),
        search: (
            <div />
        )
    }
}
export default usePostSelection;